// src/pages/subscription/BundleSubscriptionDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionBundle, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return String(dateStr); }
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function mapStatusToVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'SUBSCRIPTION_STATUS_ACTIVE': return 'success';
    case 'SUBSCRIPTION_STATUS_TRIAL': return 'primary';
    case 'SUBSCRIPTION_STATUS_PAST_DUE': return 'warning';
    case 'SUBSCRIPTION_STATUS_CANCELLED': return 'error';
    case 'SUBSCRIPTION_STATUS_SUSPENDED': return 'warning';
    case 'SUBSCRIPTION_STATUS_EXPIRED': return 'secondary';
    default: return 'secondary';
  }
}

export const BundleSubscriptionDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionBundle();
  const { t } = useI18n();
  const [item, setItem] = useState<any>(null);

  useEffect(() => {
    if (id) {
      store.GetBundleSubscription(id)
        .then((res) => setItem(res?.subscription ?? null))
        .catch(() => {});
    }
    return () => { setItem(null); };
  }, [id]);

  const handleCancel = async () => {
    if (!id || !confirm(t('subscription.bundleSubscription.cancelConfirm'))) return;
    try {
      await store.CancelBundleSubscription(id);
      navigate('/subscriptions/bundles/subscriptions');
    } catch {
      // Error handled by store
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/subscriptions/bundles/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/subscriptions/bundles/subscriptions" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const selectedProductsRaw = item.selected_products?.items;
  const selectedProducts = Array.isArray(selectedProductsRaw) ? selectedProductsRaw : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
              {t('subscription.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to="/subscriptions/bundles/subscriptions" className="text-muted-foreground hover:text-foreground">
              {t('subscription.bundleSubscription.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('subscription.bundleSubscription.details')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('subscription.bundleSubscription.details')}</h1>
        </div>
        <div className="flex gap-2">
          {item.status !== 'SUBSCRIPTION_STATUS_CANCELLED' && item.status !== 'SUBSCRIPTION_STATUS_EXPIRED' && (
            <Button variant="danger" onClick={handleCancel}>
              {t('subscription.bundleSubscription.cancel')}
            </Button>
          )}
        </div>
      </div>

      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.bundleSubscription.overview')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.bundle')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.bundle?.entity?.id ? (
                  <Link to={`/subscriptions/bundles/${item.bundle.entity.id}`} className="text-primary hover:underline">
                    {String(item.bundle?.entity?.id ?? '--')}
                  </Link>
                ) : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.tenant')}</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.tenant?.entity?.id ?? item.tenant ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.status')}</dt>
              <dd className="text-sm col-span-2">
                <Badge variant={mapStatusToVariant(item.status)} size="sm">
                  {formatLabel(String(item.status ?? ''), 'SUBSCRIPTION_STATUS_')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.owner')}</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.owner?.entity?.id ?? item.owner ?? '--')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.bundleSubscription.pricing')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.totalPrice')}</dt>
              <dd className="text-sm text-foreground col-span-2 font-semibold">{formatMoney(item.total_price)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.originalPrice')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatMoney(item.original_price)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.discountAmount')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.discount_amount ? `-${formatMoney(item.discount_amount)}` : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.currency')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatLabel(String(item.currency ?? ''), 'CURRENCY_')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Period */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.bundleSubscription.period')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.currentPeriod')}</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate(item.current_period_start)} — {formatDate(item.current_period_end)}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.startedAt')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.started_at)}</dd>
            </div>
            {item.ends_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.endsAt')}</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDate(item.ends_at)}</dd>
              </div>
            )}
            {item.trial_end && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.trialEnd')}</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDate(item.trial_end)}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Cancellation */}
      {item.cancelled_at && (
        <Card>
          <CardHeader>
            <CardTitle>{t('subscription.bundleSubscription.cancellation')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border">
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.cancelledAt')}</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDate(item.cancelled_at)}</dd>
              </div>
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.cancellationReason')}</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {item.cancellation_reason ? formatLabel(String(item.cancellation_reason), 'CANCELLATION_REASON_') : '--'}
                </dd>
              </div>
              {item.cancellation_comment && (
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">{t('subscription.bundleSubscription.field.cancellationComment')}</dt>
                  <dd className="text-sm text-foreground col-span-2">{String(item.cancellation_comment)}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Selected Products */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.bundleSubscription.selectedProducts')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('subscription.bundleSubscription.field.product')}</TableHead>
                <TableHead>{t('subscription.bundleSubscription.field.plan')}</TableHead>
                <TableHead>{t('subscription.bundleSubscription.field.subscription')}</TableHead>
                <TableHead>{t('subscription.bundleSubscription.field.active')}</TableHead>
                <TableHead>{t('subscription.bundleSubscription.field.quantity')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedProducts.length === 0 ? (
                <TableEmpty colSpan={5} message={t('subscription.bundleSubscription.noProducts')} />
              ) : (
                selectedProducts.map((prod: any, idx: number) => (
                  <TableRow key={prod.product?.entity?.id || idx}>
                    <TableCell>{String(prod.product?.entity?.id ?? '--')}</TableCell>
                    <TableCell>{String(prod.plan?.entity?.id ?? '--')}</TableCell>
                    <TableCell>
                      {prod.subscription?.entity?.id ? (
                        <Link to={`/subscriptions/${prod.subscription.entity.id}`} className="text-primary hover:underline">
                          {String(prod.subscription.entity.id)}
                        </Link>
                      ) : '--'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={prod.is_active ? 'success' : 'secondary'} size="sm">
                        {prod.is_active ? t('common.yes') : t('common.no')}
                      </Badge>
                    </TableCell>
                    <TableCell>{String(prod.quantity ?? '--')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.bundleSubscription.timestamps')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('common.createdAt')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">{t('common.updatedAt')}</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default BundleSubscriptionDetailPage;
